<template>
  <v-app-bar app flat color="white" class="main-header">
    <v-container class="nav-inner">
      <v-row align="center" no-gutters>
        <!-- Logo -->
        <v-col cols="auto" class="d-flex align-center">
          <router-link to="/" class="logo-link">
            <v-img :src="logo" height="40" width="200" contain />
          </router-link>
        </v-col>

        <!-- Nav links -->
        <v-col class="d-flex justify-center">
          <div class="nav-links">
            <v-btn v-for="link in navLinks" :key="link.label" :to="link.to" variant="text" class="nav-link"
              :class="{ 'nav-link--active': $route.path === link.to }">
              {{ link.label }}
            </v-btn>
          </div>
        </v-col>

        <!-- Auth buttons / User menu -->
        <v-col cols="auto" class="d-flex justify-end align-center">
          <!-- Not logged in -->
          <div v-if="!user">
            <v-btn :to="{ path: '/login' }" class="sign-in-btn" rounded="pill" height="40">
              Sign-in
            </v-btn>
          </div>

          <!-- Logged in -->
          <div v-else class="d-flex align-center">
            <v-menu v-model="userMenu" location="bottom end" offset="8">
              <template #activator="{ props }">
                <v-btn v-bind="props" class="user-avatar-btn" variant="outlined" rounded="pill" size="small"
                  elevation="0">
                  <v-avatar size="36" class="me-2" :class="{ 'user-avatar--initials': !avatarUrl }">
                    <v-img v-if="avatarUrl" :src="avatarUrl" alt="Profile" cover />
                    <span v-else class="avatar-initials">{{ userInitials }}</span>
                  </v-avatar>

                  <v-icon size="18" class="chevron-icon">mdi-chevron-down</v-icon>
                </v-btn>
              </template>


              <v-card min-width="220">
                <v-list density="comfortable">

                  <v-list-item @click="goToBookings" prepend-icon="mdi-ticket-outline">
                    <v-list-item-title>Bookings</v-list-item-title>
                  </v-list-item>

                  <v-list-item @click="goTowishlist" prepend-icon="mdi-heart-outline">
                    <v-list-item-title>Wishlists</v-list-item-title>
                  </v-list-item>

                  <v-list-item @click="goToAccount" prepend-icon="mdi-message-outline">
                    <v-list-item-title>Messages</v-list-item-title>
                  </v-list-item>

                  <v-list-item @click="goToProfile" prepend-icon="mdi-account-circle-outline">
                    <v-list-item-title>Profile</v-list-item-title>
                  </v-list-item>

                  <v-list-item @click="goToAccount" prepend-icon="mdi-help-circle-outline">
                    <v-list-item-title>Help</v-list-item-title>
                  </v-list-item>

                  <v-divider class="my-1" />

                  <v-list-item @click="onLogoutClick" :disabled="signingOut">
                    <v-list-item-title class="text-error">
                      {{ signingOut ? 'Logging out…' : 'Logout' }}
                    </v-list-item-title>
                  </v-list-item>
                </v-list>
              </v-card>
            </v-menu>
          </div>
        </v-col>
      </v-row>
    </v-container>
  </v-app-bar>
</template>

<script setup>
import { computed, ref } from 'vue'
import { useStore } from 'vuex'
import { useRouter } from 'vue-router'
import logoSrc from '@/assets/logo.png'

const store = useStore()
const router = useRouter()

const user = computed(() => store.state.user)
const isAdmin = computed(() => !!(user.value?.isAdmin ?? user.value?.is_admin))
const isAgent = computed(() => !!user.value?.is_agent)

const logo = logoSrc

const navLinks = [
  { label: 'Home', to: '/' },
  { label: 'About', to: '/about' },
  { label: 'Deals', to: '/deals' },
  { label: 'Contact', to: '/contact' }
]

const signingOut = ref(false)
const userMenu = ref(false)

const accountRoute = computed(() =>
  isAgent.value || isAdmin.value ? '/dashboard/home' : '/dashboard/bookings'
)

// avatar URL if you store one on the user
const avatarUrl = computed(
  () => user.value?.avatar_url || user.value?.avatarUrl || null
)

/*
const userEmail = computed(() => user.value?.email || '')
*/

const userInitials = computed(() => {
  const u = user.value || {}
  const first = u.first_name || u.firstName || ''
  const last = u.last_name || u.lastName || ''
  const name = (first + ' ' + last).trim()

  if (name) {
    const parts = name.split(' ')
    return parts
      .slice(0, 2)
      .map(p => p.charAt(0).toUpperCase())
      .join('')
  }

  if (u.email) return u.email.charAt(0).toUpperCase()
  return '?'
})

function goToAccount() {
  userMenu.value = false
  router.push(accountRoute.value)
}

function goToBookings() {
  userMenu.value = false
  router.push("/account/bookings")
}

function goToProfile() {
  userMenu.value = false
  router.push("/account/profile")
}

function goTowishlist() {
  userMenu.value = false
  router.push("/account/wishlist")
}

async function handleLogout() {
  if (signingOut.value) return
  signingOut.value = true
  try {
    await store.dispatch('signOut')
    await router.replace('/')
  } catch (e) {
    console.error('Logout error:', e)
    await router.replace('/')
  } finally {
    signingOut.value = false
  }
}

async function onLogoutClick() {
  userMenu.value = false
  await handleLogout()
}
</script>

<style scoped>
.main-header {
  border-bottom: 1px solid #f0f0f0;
}

.nav-inner {
  max-width: 1120px;
}

/* Logo */
.logo-link {
  display: inline-flex;
  align-items: center;
  text-decoration: none;
}

/* NAV LINKS */
.nav-links {
  display: flex;
  align-items: center;
  gap: 32px;
}

.nav-link {
  text-transform: none;
  font-size: 0.95rem;
  font-weight: 500;
  letter-spacing: 0;
  color: #4a4a4a;
  min-width: auto;
}

.nav-link--active {
  font-weight: 700;
  color: #222222;
}

/* SIGN-IN BUTTON */
.sign-in-btn {
  background-color: #282e98 !important;
  color: #ffffff !important;
  text-transform: none;
  font-weight: 600;
  padding-inline: 24px;
}

/* User avatar button */
.user-avatar-btn {
  background-color: #ffffff !important;
  border: 1px solid #e0e0e0 !important;
  padding-inline: 6px 10px !important;
  min-width: auto;
  height: 42px !important;
  transition: background-color 0.2s ease, box-shadow 0.2s ease;
}

.user-avatar-btn:hover {
  background-color: #f7f7f7 !important;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.08);
}

.user-avatar--initials {
  background-color: #007a8c;
  /* teal fill like your screenshot */
  color: #fff;
  font-weight: 700;
  border: 2px solid white;
}

.avatar-initials {
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 0.8rem;
  text-transform: uppercase;
  width: 100%;
  height: 100%;
}

.chevron-icon {
  color: #111;
  transition: transform 0.2s ease;
}

/* Rotate the chevron when menu opens */
.v-menu--active .chevron-icon {
  transform: rotate(180deg);
}

/* Small screens */
@media (max-width: 960px) {
  .nav-inner {
    max-width: 100%;
  }

  .nav-links {
    gap: 16px;
  }

  .user-name {
    max-width: 100px;
  }
}
</style>
